// src/components/InstallerTracker.js
import React, { useEffect, useMemo, useState, useCallback } from 'react';
import axios from 'axios';
import { API_BASE_URL } from '../config';

// ---- Hard-coded products ----
const PRODUCTS = [
  { key: 'proload', label: 'Proload', id: 'a9ccd26b-412b-4f85-8f73-af26c08cbea5' },
  { key: 'dopex',   label: 'DOPEX', id: '5ba3845b-c38c-4d48-b150-a2c379d67dd0' },
];

const initialCreate = { url: '', version: '', adminNotes: '' };
const initialEdit   = { id: null, url: '', version: '', isActive: true, isDeleted: false, adminNotes: '' };

export default function InstallerTracker() {
  // Selected product
  const [productKey, setProductKey] = useState(PRODUCTS[0].key);
  const selectedProduct = useMemo(
    () => PRODUCTS.find(p => p.key === productKey) || PRODUCTS[0],
    [productKey]
  );
  const productId = selectedProduct.id;

  // Data + UI
  const [installers, setInstallers] = useState([]);
  const [currentInstaller, setCurrentInstaller] = useState(null);
  const [loading, setLoading] = useState(false);
  const [err, setErr] = useState(null);

  // Create modal
  const [createOpen, setCreateOpen] = useState(false);
  const [createForm, setCreateForm] = useState(initialCreate);

  // Edit modal
  const [editOpen, setEditOpen] = useState(false);
  const [editForm, setEditForm] = useState(initialEdit);

  // Columns
  const headers = useMemo(() => ([
    { key: 'id',        label: 'ID' },
    { key: 'version',   label: 'Version' },
    { key: 'url',       label: 'URL' },
    { key: 'isActive',  label: 'Active' },
    { key: 'isDeleted', label: 'Deleted' },
    { key: 'adminNotes',label: 'Admin Notes' },
    { key: 'actions',   label: '' }
  ]), []);

  const fetchAll = useCallback(async (pid) => {
    if (!pid) return;
    setLoading(true); setErr(null);
    try {
      const [listRes, singleRes] = await Promise.all([
        axios.get(`${API_BASE_URL}/api/installers`, { params: { ProductId: pid } }),
        axios.get(`${API_BASE_URL}/api/installer`,  { params: { ProductId: pid } })
      ]);
      const list = Array.isArray(listRes.data) ? listRes.data : (listRes.data?.items ?? []);
      setInstallers(list);
      setCurrentInstaller(singleRes.data ?? null);
    } catch (e) {
      console.error(e);
      setErr(e?.response?.data ?? 'Failed to load installers');
    } finally {
      setLoading(false);
    }
  }, []);

  // Auto-load whenever product changes
  useEffect(() => { fetchAll(productId); }, [productId, fetchAll]);

  // Create
  const handleCreate = async (e) => {
    e.preventDefault();
    const body = {
      productId,
      url: createForm.url,
      version: createForm.version,
      adminNotes: createForm.adminNotes
    };
    if (!body.productId) { setErr('ProductId is required.'); return; }
    setLoading(true); setErr(null);
    try {
      await axios.post(`${API_BASE_URL}/api/installer`, body);
      setCreateOpen(false);
      setCreateForm(initialCreate);
      await fetchAll(productId);
    } catch (e) {
      console.error(e);
      setErr(e?.response?.data ?? 'Failed to create installer');
    } finally {
      setLoading(false);
    }
  };

  // Edit
  const openEdit = (row) => {
    setEditForm({
      id: row.id,
      url: row.url ?? '',
      version: row.version ?? '',
      isActive: !!row.isActive,
      isDeleted: !!row.isDeleted,
      adminNotes: row.adminNotes ?? ''
    });
    setEditOpen(true);
  };

  const handleEdit = async (e) => {
    e.preventDefault();
    if (editForm.id == null) return;
    setLoading(true); setErr(null);
    try {
      await axios.patch(`${API_BASE_URL}/api/installer`, {
        id: editForm.id,
        url: editForm.url,
        version: editForm.version,
        isActive: editForm.isActive,
        isDeleted: editForm.isDeleted,
        adminNotes: editForm.adminNotes
      });
      setEditOpen(false);
      await fetchAll(productId);
    } catch (e) {
      console.error(e);
      setErr(e?.response?.data ?? 'Failed to update installer');
    } finally {
      setLoading(false);
    }
  };

  // Delete
  const handleDelete = async (id) => {
    if (!window.confirm('Are you sure you want to delete this installer? This cannot be undone.')) return;
    setLoading(true); setErr(null);
    try {
      await axios.delete(`${API_BASE_URL}/api/installer`, { params: { Id: id } });
      await fetchAll(productId);
    } catch (e) {
      console.error(e);
      setErr(e?.response?.data ?? 'Failed to delete installer');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div style={{ padding: 16 }}>
      <h2 style={{ margin: '8px 0' }}>Installers</h2>

      {/* Product selector + read-only Product ID */}
      <div style={{ display:'flex', gap:12, alignItems:'flex-end', flexWrap:'wrap', marginBottom:12 }}>
        <div>
          <label style={labelStyle}>Product</label>
          <select
            value={productKey}
            onChange={(e) => setProductKey(e.target.value)}
            style={inputStyle}
          >
            {PRODUCTS.map(p => (
              <option key={p.key} value={p.key}>{p.label}</option>
            ))}
          </select>
        </div>
        <div style={{ flex: '1 1 420px', maxWidth: 600 }}>
          <label style={labelStyle}>Product ID</label>
          <input type="text" value={productId} readOnly style={{ ...inputStyle, width: '100%' }} />
        </div>
        <button
          type="button"
          onClick={() => { setCreateForm(initialCreate); setCreateOpen(true); }}
          style={btnPrimary}
        >
          New Installer
        </button>
        <button
          type="button"
          onClick={() => fetchAll(productId)}
          disabled={loading}
          style={btnGhost}
        >
          Refresh
        </button>
      </div>

      {/* Optional: Current Installer panel */}
      <div style={{ marginBottom:16, padding:12, border:'1px solid #eee', borderRadius:8, background:'#fafafa' }}>
        <div style={{ fontWeight:600, marginBottom:6 }}>Current Installer</div>
        {currentInstaller ? (
          <div style={{ display:'grid', gridTemplateColumns:'repeat(6, minmax(0,1fr))', gap:8, fontSize:14 }}>
            <div><b>Version:</b> {currentInstaller.version}</div>
            <div style={{ gridColumn:'span 3' }}>
              <b>URL:</b>{' '}
              {currentInstaller.url ? (
                <>
                  <a href={currentInstaller.url} target="_blank" rel="noreferrer">{currentInstaller.url}</a>
                  <button
                    type="button"
                    onClick={() => navigator.clipboard.writeText(currentInstaller.url)}
                    style={{
                      padding:'2px 6px',
                      border:'1px solid #ccc',
                      borderRadius:4,
                      background:'#f9f9f9',
                      cursor:'pointer',
                      fontSize:12
                    }}
                    title="Copy URL to clipboard"
                  >
                    Copy
                  </button>
                </>
              ) : '—'}
            </div>
          </div>
        ) : (
          <div style={{ fontSize:14, color:'#666' }}>No active installer returned.</div>
        )}
      </div>

      {/* Installers Table */}
      <div style={{ overflowX:'auto', border:'1px solid #eee', borderRadius:8 }}>
        <table style={{ width:'100%', borderCollapse:'collapse' }}>
          <thead>
            <tr style={{ background:'#f6f6f6' }}>
              {headers.map(h => (
                <th key={h.key} style={{ padding:10, textAlign:'left', fontWeight:600, borderBottom:'1px solid #eee' }}>
                  {h.label}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {loading ? (
              <tr><td colSpan={headers.length} style={{ padding:16 }}>Loading…</td></tr>
            ) : installers.length === 0 ? (
              <tr><td colSpan={headers.length} style={{ padding:16, color:'#666' }}>No installers found.</td></tr>
            ) : (
              installers.map(row => (
                <tr
                  key={row.id}
                  style={{
                    borderTop:'1px solid #f0f0f0',
                    background: row.isActive ? '#e9f9ee' : 'transparent' // highlight active row green-ish
                  }}
                >
                  <td style={{ padding:10 }}>{row.id}</td>
                  <td style={{ padding:10 }}>{row.version}</td>
                  <td style={{ padding:10, maxWidth:360, overflow:'hidden', textOverflow:'ellipsis', whiteSpace:'nowrap' }}>
                    {row.url ? <a href={row.url} target="_blank" rel="noreferrer">{row.url}</a> : '—'}
                  </td>
                  <td style={{ padding:10 }}>{row.isActive ? 'Yes' : 'No'}</td>
                  <td style={{ padding:10 }}>{row.isDeleted ? 'Yes' : 'No'}</td>
                  <td style={{ padding:10 }}>{row.adminNotes || ''}</td>
                  <td style={{ padding:10, whiteSpace:'nowrap' }}>
                    <div style={{ display:'flex', gap:8 }}>
                      <button
                        type="button"
                        onClick={() => openEdit(row)}
                        style={{ padding:'6px 10px', border:'1px solid #ddd', background:'#fff', borderRadius:6, cursor:'pointer' }}
                      >
                        Edit
                      </button>
                      <button
                        type="button"
                        onClick={() => handleDelete(row.id)}
                        style={{ padding:'6px 10px', border:'1px solid #e00', color:'#e00', background:'#fff', borderRadius:6, cursor:'pointer' }}
                      >
                        Delete
                      </button>
                    </div>
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>

      {/* Error */}
      {err && <div style={{ marginTop:12, color:'#b00020', fontSize:14 }}>{String(err)}</div>}

      {/* Create Modal */}
      {createOpen && (
        <div style={modalWrapStyle}>
          <div style={modalStyle}>
            <div style={modalHeadStyle}>
              <div style={{ fontWeight:700 }}>New Installer</div>
              <button type="button" onClick={() => setCreateOpen(false)} style={xBtnStyle}>×</button>
            </div>

            {/* Product name + ID at top */}
            <div style={{ display:'grid', gap:8, marginBottom:8, padding: '0 4px' }}>
              <div><b>Product:</b> {selectedProduct.label}</div>
              <div><b>Product ID:</b> {productId}</div>
            </div>

            <form onSubmit={handleCreate} style={formStyle}>
              <div style={fieldStyle}>
                <label style={labelStyle}>Version</label>
                <input
                  value={createForm.version}
                  onChange={(e)=>setCreateForm({...createForm, version:e.target.value})}
                  style={inputStyle}
                  required
                />
              </div>
              <div style={fieldStyle}>
                <label style={labelStyle}>URL</label>
                <input
                  value={createForm.url}
                  onChange={(e)=>setCreateForm({...createForm, url:e.target.value})}
                  style={inputStyle}
                  required
                />
              </div>
              <div style={fieldStyle}>
                <label style={labelStyle}>Admin Notes</label>
                <textarea
                  value={createForm.adminNotes}
                  onChange={(e)=>setCreateForm({...createForm, adminNotes:e.target.value})}
                  style={{ ...inputStyle, minHeight: 120, resize: 'vertical' }}
                />
              </div>
              <div style={{ display:'flex', gap:8, justifyContent:'flex-end' }}>
                <button type="button" onClick={()=>setCreateOpen(false)} style={btnGhost}>Cancel</button>
                <button type="submit" disabled={loading} style={btnPrimary}>Create</button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Edit Modal */}
      {editOpen && (
        <div style={modalWrapStyle}>
          <div style={modalStyle}>
            <div style={modalHeadStyle}>
              <div style={{ fontWeight:700 }}>Edit Installer #{editForm.id}</div>
              <button type="button" onClick={() => setEditOpen(false)} style={xBtnStyle}>×</button>
            </div>
            <form onSubmit={handleEdit} style={formStyle}>
              <div style={fieldStyle}>
                <label style={labelStyle}>Version</label>
                <input
                  value={editForm.version}
                  onChange={(e)=>setEditForm({...editForm, version:e.target.value})}
                  style={inputStyle}
                  required
                />
              </div>
              <div style={fieldStyle}>
                <label style={labelStyle}>URL</label>
                <input
                  value={editForm.url}
                  onChange={(e)=>setEditForm({...editForm, url:e.target.value})}
                  style={inputStyle}
                  required
                />
              </div>
              <div style={{ ...fieldStyle, display:'flex', gap:18, alignItems:'center' }}>
                <label style={{ display:'flex', gap:6, alignItems:'center' }}>
                  <input
                    type="checkbox"
                    checked={editForm.isActive}
                    onChange={(e)=>setEditForm({...editForm, isActive:e.target.checked})}
                  />
                  Active
                </label>                
              </div>
              <div style={fieldStyle}>
                <label style={labelStyle}>Admin Notes</label>
                <textarea
                  value={editForm.adminNotes}
                  onChange={(e)=>setEditForm({...editForm, adminNotes:e.target.value})}
                  style={{ ...inputStyle, minHeight: 120, resize: 'vertical' }}
                />
              </div>
              <div style={{ display:'flex', gap:8, justifyContent:'flex-end' }}>
                <button type="button" onClick={()=>setEditOpen(false)} style={btnGhost}>Cancel</button>
                <button type="submit" disabled={loading} style={btnPrimary}>Save</button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}

/* — styles — */
const labelStyle = { display:'block', fontSize:12, color:'#555', marginBottom:4 };

const inputStyle = {
  width: '100%',
  padding: 10,
  border: '1px solid #ddd',
  borderRadius: 8,
  fontSize: 14,
  boxSizing: 'border-box',
  minWidth: 0,
};

const btnPrimary = { padding:'10px 14px', border:'none', borderRadius:8, background:'#111', color:'#fff', cursor:'pointer' };
const btnGhost   = { padding:'10px 14px', border:'1px solid #ddd', borderRadius:8, background:'#fff', cursor:'pointer' };

// Modals: wider + inner padding so fields never touch the edges
const modalWrapStyle = {
  position:'fixed',
  inset:0,
  background:'rgba(0,0,0,0.35)',
  display:'flex',
  alignItems:'center',
  justifyContent:'center',
  zIndex: 1000
};

const modalStyle = {
  width: 820,
  maxWidth:'95vw',
  maxHeight:'90vh',
  overflow:'auto',
  background:'#fff',
  borderRadius:12,
  padding:24,                  // inner padding around the whole modal
  boxSizing:'border-box',
  boxShadow:'0 10px 30px rgba(0,0,0,.18)'
};

const modalHeadStyle = { display:'flex', justifyContent:'space-between', alignItems:'center', marginBottom:12 };
const xBtnStyle = { background:'transparent', border:'none', fontSize:22, lineHeight:1, cursor:'pointer' };

const formStyle = {
  display: 'grid',
  gap: 12,
  padding: '0 4px 8px',        // consistent inner padding for form fields
};

const fieldStyle = {
  padding: '6px 0',           // space above/below each field group
};
